import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, } from "@mui/material";
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DataTable from '../../components/common/DataTable';
import MessageBox from "../../components/common/CustomMessageBox";
import CustomDelete from '../../components/common/CustomDeleteAlert';
import CustomModal from "../../components/common/CustomModal";
import { getCurrencyList } from '../../services/CommonServices';
import { ENQUIRY_COMPANY_DELETE, ENQUIRY_SUBSCRIPTION_PLAN, ENQUIRY_SUBSCRIPTION_PLAN_DELETE, ENQUIRY_SUBSCRIPTION_PLAN_UPDATE, } from "../../services/apiRoutes";
import { genericGet } from "../../services/apiServices";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import CustomLoader from "../../components/common/CustomLoader";
import { hasEditPermission, hasDeletePermission } from '../../services/CommonServices'
import { useLocation } from "react-router-dom";
import CreateSubscriptionPlan from "../components/CreateForm/CreateSubscriptionPlan";
import UpdateSubscriptionPlan from "../components/UpdateForm/UpdateSubscriptionPlan";

const title = 'Subscription Plan';

const EnquirySubscriptionPlan = () => {
    const { token } = useContext(AuthContext)
    const [modal, setModal] = useState(false);
    const [updateModal, setUpdateModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [clientCompanies, setClientCompanies] = useState([]);
    const [owner, setOwner] = useState([]);
    const [message, setMessage] = useState({
        open: false,
        text: '',
        type: ''
    })
    const [deleteAlert, setDeleteAlert] = useState(false);
    const [rowId, setRowId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [permission, setPermissions] = useState()
    const [currencyList, setCurrencyList] = useState([]);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);

    const handleUpdateClick = (clientCompanyId) => {
        setSelectedId(clientCompanyId);
        setUpdateModal(true);
    };

    const handleOpenDeleteAlert = (rowId) => {
        setDeleteAlert(true);
        setRowId(rowId);
    };

    const handleCloseUpdate = () => {
        setUpdateModal(false);
    };

    const handleDelete = async (subscriptionPlanId) => {
        setLoading(true);
        const authToken = sessionStorage.getItem("accessToken");

        try {
            const response = await axios.delete(
                ENQUIRY_SUBSCRIPTION_PLAN_DELETE(subscriptionPlanId),
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Deleted successfully",
                    type: "success",
                });
                setDeleteAlert(false);
                fetchSubscriptionPlans();
            }

        } catch (error) {
            console.error("Delete error:", error);

            // ⭐ Handle only 403 error
            if (error?.response?.status === 403) {
                setMessage({
                    open: true,
                    text: error?.response?.data?.message || "Plan is active, cannot delete",
                    type: "error",
                });
            }

            // Default fallback
            else {
                setMessage({
                    open: true,
                    text: "Network or server error. Try again.",
                    type: "error",
                });
            }

        } finally {
            setLoading(false);
        }
    };

    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        setPermissions({
            edit: editPermision,
            delete: deletePermission
        })
    }, [])

    useEffect(() => {
        fetchClientCompanies()
        fetchSubscriptionPlans()
    }, [token])

    useEffect(() => {
        getCommonData();
    }, []);

    const handleToggleStatus = async (subscriptionPlanId, currentStatus) => {
        const newStatus = currentStatus === "true" ? "false" : "true";
        const accessToken = sessionStorage.getItem('accessToken');

        try {
            const response = await axios.put(
                ENQUIRY_SUBSCRIPTION_PLAN_UPDATE(subscriptionPlanId),
                { planActiveStatus: newStatus },
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }
            );

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: "updated successfully",
                    type: "success",
                });
                fetchSubscriptionPlans();
            } else {
                throw new Error("Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            setMessage({
                open: true,
                text: "Error updating status",
                type: "error",
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: "index",
                header: "SN.",
                size: 50,
                tableHeader: { alignItems: 'center' },
                tableStyle: { textAlign: "center" },
                enableColumnFilter: false,
            },
            {
                accessorKey: "planName",
                header: "plan",
                size: 150,
                enableSorting: false,
                tableHeader: { justifyContent: 'flex-start' },
            },
            {
                accessorKey: "baseAmount",
                header: "base Amount",
                size: 150,
                enableSorting: false,
                tableHeader: { justifyContent: 'flex-start' },
            },
            {
                accessorKey: "perMonthAmount",
                header: "user Per Month",
                size: 150,
                enableSorting: false,
                tableHeader: { justifyContent: 'flex-start' },
            },

            {
                accessorKey: "companyPerMonth",
                header: "company Per Month",
                size: 150,
                enableSorting: false,
                tableHeader: { justifyContent: 'flex-start' },
            },

            {
                accessorKey: "planActiveDate",
                header: "plan Active",
                size: 150,
                enableSorting: false,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: "planExpiredDate",
                header: "plan Expired",
                size: 150,
                enableSorting: false,
                tableHeader: { justifyContent: 'flex-start' },
            },
            {
                accessorKey: "planActiveStatus",
                header: "Status",
                size: 120,
                Cell: ({ row }) => {
                    const status = row.original.planActiveStatus;
                    const isActive = status === true;
                    const subscriptionPlanId = row.original.subscriptionPlanId;

                    return (
                        <Button
                            variant="outlined"
                            onClick={() => handleToggleStatus(subscriptionPlanId, status)}
                            sx={{
                                color: isActive ? 'green' : 'red',
                                borderColor: isActive ? 'green' : 'red',
                                fontWeight: 100,
                                textAlign: 'center',
                                cursor: 'pointer',
                                width: "80px",
                                height: "30px",
                                fontSize: "12px",
                                textTransform: "capitalize",
                                '&:hover': {
                                    borderColor: isActive ? 'darkgreen' : 'darkred',
                                    color: isActive ? 'darkgreen' : 'darkred',
                                },
                            }}
                        >
                            {isActive ? 'Activate' : 'Deactivate'}
                        </Button>
                    );
                },
                tableStyle: { textAlign: 'center' },
            },
            {
                accessorKey: "action",
                header: "Action",
                size: 100,
                enableSorting: false,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>

                        {permission?.edit && (
                            <IconButton onClick={() => handleUpdateClick(row.original.id)}>
                                <ModeEditOutlineOutlinedIcon style={{ color: "#050C9C" }} />
                            </IconButton>
                        )}
                        {permission?.delete && (
                            <IconButton onClick={() => handleOpenDeleteAlert(row.original.id)}>
                                <DeleteOutlineOutlinedIcon style={{ color: "#E4003A" }} />
                            </IconButton>
                        )}
                    </div>
                ),
            },
        ],
        [permission]
    );


    const fetchClientCompanies = async () => {
        setLoading(true);
        try {
            const response = await genericGet({ url: ENQUIRY_SUBSCRIPTION_PLAN });

            if (response.data.success && response.data.enquiryCompany) {
                const clientCompanyData = response.data.enquiryCompany.map((company, index) => ({
                    clientCompanyId: company.clientCompanyId || '',
                    index: (index + 1).toString().padStart(2, '0'),
                    clientCompanyName: company.companyName || '',
                    phone: company.telephone || '',
                    mobile: company.mobile || '',
                    email: company.email || '',
                    address: `${company.addressLineOne || ''} ${company.addressLineTwo || ''} ${company.address || ''}`.trim(),
                    companyAdmin: `${company.user?.firstName || ''} ${company.user?.lastName || ''} `.trim(),
                    country: company.country?.name || '',
                    contacts: '',
                }));
                setClientCompanies(clientCompanyData);
            } else {
                setError('No client companies found');
            }
        } catch (error) {
            setError('Error fetching client companies');
            console.error('Error fetching client companies', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchSubscriptionPlans = async () => {
        setLoading(true);
        try {
            const response = await genericGet({ url: ENQUIRY_SUBSCRIPTION_PLAN });

            if (response.data.success && Array.isArray(response.data.SubscriptionPlan)) {

                const planData = response.data.SubscriptionPlan.map((plan, index) => ({
                    id: plan.subscriptionPlanId,
                    index: (index + 1).toString().padStart(2, '0'),
                    perMonthAmount: `${plan.currencyList?.currency_symbol || ""}${plan.perMonthAmount || ""}`,
                    companyPerMonth: `${plan.currencyList?.currency_symbol || ""}${plan.companyPerMonth || ""}`,
                    baseAmount: `${plan.currencyList?.currency_symbol || ""}${plan.baseAmount || ""}`,
                    planActiveDate: plan.planActiveDate || '',
                    planExpiredDate: plan.planExpiredDate || '',
                    subscriptionPlanId: plan.subscriptionPlanId || "",
                    planActiveStatus: plan.planActiveStatus || "",
                    planName: plan.PlanType?.planName || "",

                }));

                setSubscriptionPlans(planData);
            } else {
                setError('No subscription plans found');
            }
        } catch (error) {
            setError('Error fetching subscription plans');
            console.error('Error fetching subscription plans:', error);
        } finally {
            setLoading(false);
        }
    };
    const getCommonData = async () => {
        const currencyListData = await getCurrencyList();
        setCurrencyList(currencyListData);
    };

    const handleOpen = () => {
        setModal(true);
    };

    const handleClose = () => {
        setModal(false);
    };


    return (
        <>
            <Box sx={{ padding: '10px' }}>
                <CustomLoader open={loading} />
                <DataTable
                    title={title}
                    columns={columns}
                    data={subscriptionPlans}
                    handleOpen={handleOpen}
                    buttonTitle="Create"

                />
                <CustomModal open={modal}  >
                    <CreateSubscriptionPlan
                        handleClose={handleClose}
                        owner={owner}
                        fetchData={fetchSubscriptionPlans}
                        currencyList={currencyList}
                        setMessage={setMessage}
                    />
                </CustomModal>

                <CustomModal open={updateModal}>
                    <UpdateSubscriptionPlan
                        subscriptionPlanId={selectedId}
                        handleClose={handleCloseUpdate}
                        fetchData={fetchSubscriptionPlans}
                        currencyList={currencyList}
                        setMessage={setMessage}
                    />

                </CustomModal>

                <MessageBox
                    open={message.open}
                    onClose={() => setMessage({ ...message, open: false })}
                    message={message.text}
                    handleClose={() => setMessage({ ...message, open: false })}
                    type={message.type}
                />
                <CustomDelete
                    handleDelete={handleDelete}
                    open={deleteAlert}
                    rowId={rowId}
                    handleClose={() => setDeleteAlert(false)}
                />

            </Box>
        </>
    );
};

export default EnquirySubscriptionPlan;
